import { useAppDispatch } from '@/app/hooks';
import DeleteFileBtn from '@/components/MantineFileManager/btn/DeleteFileBtn';
import DeleteFolderBtn from '@/components/MantineFileManager/btn/DeleteFolderBtn';
import RenameFileBtn from '@/components/MantineFileManager/btn/RenameFileBtn';
import RenameFolderBtn from '@/components/MantineFileManager/btn/RenameFolderBtn';
import type { FileManagerItem } from '@/components/MantineFileManager/file.type';
import type { DirectusFiles, DirectusFolders } from '@/models/types';
import { trackEvent } from '@/shared/analytics/trackEvent';
import { useIsAdmin } from '@/shared/auth/useDirectusUser';
import { formatDateTime } from '@/shared/utils/date-utils';
import { formatFileSize, getRelationId } from '@/shared/utils/file.utils';
import { Badge, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconFile, IconFolder, IconFolderOff } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { useMemo } from 'react';
import { setSelectedItemIds } from '../../features/drive/driveSlice';

type Props = {
  currentFolderId: string;
  files: DirectusFiles[];
  folders: DirectusFolders[];
  selectedItemIds: string[];
  onOpenFolder: (folderId: string) => void;
};

const FileDataTable = ({
  currentFolderId,
  files,
  folders,
  selectedItemIds,
  onOpenFolder,
}: Props) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const isAdmin = useIsAdmin();

  const records = useMemo(
    () =>
      [
        ...folders
          .filter(folder => getRelationId(folder.parent) === currentFolderId)
          .map<FileManagerItem>(folder => ({
            data: folder,
            id: folder.id,
            kind: 'folder',
          })),
        ...files
          .filter(file => getRelationId(file.folder) === currentFolderId)
          .map<FileManagerItem>(file => ({
            data: file,
            id: file.id,
            kind: 'file',
          })),
      ].sort((left, right) => {
        if (left.kind !== right.kind) {
          return left.kind === 'folder' ? -1 : 1;
        }

        return left.kind === 'folder'
          ? (left.data as DirectusFolders).name.localeCompare((right.data as DirectusFolders).name)
          : (left.data as DirectusFiles).filename_download.localeCompare(
              (right.data as DirectusFiles).filename_download,
            );
      }),
    [currentFolderId, files, folders],
  );

  const columns = useMemo<DataTableColumn<FileManagerItem>[]>(() => {
    const actionsColumn: DataTableColumn<FileManagerItem> = {
      accessor: 'actions',
      title: t('drive.columns.actions'),
      textAlign: 'right',
      width: 100,
      render: item => (
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          {item.kind === 'folder' ? (
            <>
              <RenameFolderBtn folder={item.data as DirectusFolders} />
              <DeleteFolderBtn folder={item.data as DirectusFolders} />
            </>
          ) : (
            <>
              <RenameFileBtn file={item.data as DirectusFiles} />
              <DeleteFileBtn file={item.data as DirectusFiles} />
            </>
          )}
        </Group>
      ),
    };

    const columns: DataTableColumn<FileManagerItem>[] = [
      {
        accessor: 'name',
        title: t('drive.columns.name'),
        ellipsis: true,
        render: item => {
          if (item.kind === 'folder') {
            const folder = item.data as DirectusFolders;

            return (
              <Group gap="xs" wrap="nowrap">
                <IconFolder size={18} />
                <UnstyledButton
                  variant="subtle"
                  p={0}
                  onClick={() => {
                    onOpenFolder(folder.id);
                  }}
                >
                  {folder.name}
                </UnstyledButton>
              </Group>
            );
          }

          const file = item.data as DirectusFiles;
          const downloadUrl = `${import.meta.env.VITE_DIRECTUS_URL}/assets/${file.id}?download`;

          return (
            <Group gap="xs" wrap="nowrap">
              <IconFile size={18} />
              <UnstyledButton
                component="a"
                href={downloadUrl}
                download={file.filename_download ?? file.title}
                onClick={() => {
                  trackEvent('drive-fichier-telecharger', {
                    type: file.type ?? 'inconnu',
                    filename: file.filename_download ?? file.title,
                  });
                }}
              >
                {file.filename_download ?? file.title}
              </UnstyledButton>
            </Group>
          );
        },
      },
      {
        accessor: 'details',
        title: t('drive.columns.details'),
        width: 120,
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.xs})`,
        textAlign: 'center',
        render: item => {
          if (item.kind === 'folder') {
            return <Badge variant="light">{t('folder.label')}</Badge>;
          }

          const file = item.data as DirectusFiles;

          return <Text size="sm">{formatFileSize(file.filesize)}</Text>;
        },
      },
      {
        accessor: 'uploaded_on',
        title: t('drive.columns.uploaded_on'),
        width: 150,
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.xs})`,
        textAlign: 'center',
        render: item => {
          if (item.kind === 'folder') {
            return <Text size="sm">-</Text>;
          }

          const file = item.data as DirectusFiles;

          return <Text size="sm">{formatDateTime(file.uploaded_on)}</Text>;
        },
      },
    ];

    if (isAdmin) {
      columns.push(actionsColumn);
    }

    return columns;
  }, [onOpenFolder, t, isAdmin]);

  const visibleRecordIds = useMemo(() => records.map(record => record.id), [records]);

  const selectedRecords = useMemo(
    () => records.filter(record => selectedItemIds.includes(record.id)),
    [records, selectedItemIds],
  );

  const emptyState = useMemo(
    () => (
      <Stack align="center" gap="xs" py="xl">
        <ThemeIcon variant="light" radius="xl" size={44} color="gray">
          <IconFolderOff size={22} />
        </ThemeIcon>
        <Text fw={500}>{t('drive.empty')}</Text>
      </Stack>
    ),
    [t],
  );

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      pinLastColumn
      withColumnBorders
      minHeight={220}
      records={records}
      idAccessor="id"
      emptyState={emptyState}
      columns={columns}
      selectedRecords={isAdmin ? selectedRecords : undefined}
      onSelectedRecordsChange={nextSelectedRecords => {
        const nextVisibleIds = nextSelectedRecords.map(record => record.id);
        const keptIds = selectedItemIds.filter(id => !visibleRecordIds.includes(id));

        dispatch(setSelectedItemIds([...keptIds, ...nextVisibleIds]));
      }}
    />
  );
};

export default FileDataTable;
